/**
 * Evidence Governance™ — one-click role export.
 *
 * Assembles the complete governance record for a single entity (current
 * enrichment, immutable version history, provenance with per-field evidence,
 * contradictions, reviewer notes and the end-to-end audit trail) and writes it
 * out as JSON or a printable PDF.
 *
 * Read-only: nothing here writes to the database, and nothing here touches
 * scoring, psychometrics, Career DNA™ or market intelligence.
 */
import jsPDF from "jspdf";
import {
  ONET_ATTRIBUTION_DOCUMENT_SENTENCE,
  onetJsonAttribution,
} from "@/lib/onetAttribution";
import type { CareerEnrichment } from "@/lib/careerEnrichment";
import {
  listEnrichmentTrace,
  listEnrichmentVersions,
  type EnrichmentEntityType,
  type EnrichmentVersion,
  type TraceEntry,
} from "@/lib/evidenceEnrichment";

export interface RoleEvidenceExport {
  exported_at: string;
  framework: string;
  /** USDOL/ETA licence condition: O*NET attribution travels with the data. */
  onet_attribution: ReturnType<typeof onetJsonAttribution>;
  entity: {
    entity_type: string;
    entity_id: string;
    name: string;
  };
  governance: {
    status: string;
    grade: string;
    promoted: boolean;
    review_status: string;
    version: number;
    confidence: number;
    trust_score: number;
    coverage: number;
    domain_count: number;
    supported_fields: number;
    freshness: string;
    hash: string | null;
    model: string | null;
    prompt_version: string | null;
    fetched_at: string | null;
    promoted_at: string | null;
    regraded_at: string | null;
    reviewed_at: string | null;
    stale: boolean;
    error: string | null;
  };
  review_notes: string | null;
  fields: Array<{
    field: string;
    value: string | string[];
    citations: string[];
    evidence: Array<{ url: string; quote: string }>;
  }>;
  provenance: CareerEnrichment["sources"];
  contradictions: CareerEnrichment["conflicts"];
  version_history: EnrichmentVersion[];
  audit_trail: TraceEntry[];
}

const slug = (s: string) =>
  (s || "role").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);

/** Gather everything the governance record contains for one entity. */
export async function buildRoleExport(
  row: CareerEnrichment,
  entityType: EnrichmentEntityType = "career",
): Promise<RoleEvidenceExport> {
  const [versions, trace] = await Promise.all([
    listEnrichmentVersions(entityType, row.entity_id).catch(() => [] as EnrichmentVersion[]),
    listEnrichmentTrace(entityType, row.entity_id, 300).catch(() => [] as TraceEntry[]),
  ]);

  const fields = Object.entries(row.payload ?? {}).map(([field, f]) => ({
    field,
    value: f?.value ?? "",
    citations: f?.sources ?? [],
    evidence: f?.evidence ?? [],
  }));

  return {
    exported_at: new Date().toISOString(),
    framework: "WeKIT Evidence Governance™ / Evidence Enrichment Framework",
    onet_attribution: onetJsonAttribution(),
    entity: { entity_type: row.entity_type, entity_id: row.entity_id, name: row.canonical_name },
    governance: {
      status: row.status,
      grade: row.grade,
      promoted: row.promoted,
      review_status: row.review_status,
      version: row.version,
      confidence: row.confidence,
      trust_score: row.trust_score,
      coverage: row.coverage,
      domain_count: row.domain_count,
      supported_fields: row.supported_fields,
      freshness: row.freshness,
      hash: row.hash,
      model: row.model,
      prompt_version: row.prompt_version,
      fetched_at: row.fetched_at,
      promoted_at: row.promoted_at,
      regraded_at: row.regraded_at,
      reviewed_at: row.reviewed_at,
      stale: row.stale,
      error: row.error,
    },
    review_notes: row.review_notes,
    fields,
    provenance: row.sources ?? [],
    contradictions: row.conflicts ?? [],
    version_history: versions,
    audit_trail: trace,
  };
}

function download(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function downloadRoleExportJson(bundle: RoleEvidenceExport): string {
  const filename = `evidence-${slug(bundle.entity.name)}-${bundle.entity.entity_id}-v${bundle.governance.version}.json`;
  download(new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" }), filename);
  return filename;
}

/* ---------------- PDF ---------------- */

const PAGE = { w: 210, h: 297, m: 16 };
const INK = { primary: [0, 102, 91] as const, body: [24, 2, 5] as const, muted: [110, 110, 110] as const };

export function downloadRoleExportPdf(bundle: RoleEvidenceExport): string {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  let y = PAGE.m;

  const page = () => {
    doc.addPage();
    y = PAGE.m;
  };
  const room = (need = 8) => {
    if (y + need > PAGE.h - PAGE.m) page();
  };
  const text = (
    value: string,
    opts: { size?: number; bold?: boolean; colour?: readonly number[]; gap?: number; indent?: number } = {},
  ) => {
    const { size = 10, bold = false, colour = INK.body, gap = 1.5, indent = 0 } = opts;
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    doc.setTextColor(colour[0], colour[1], colour[2]);
    const lines = doc.splitTextToSize(value, PAGE.w - PAGE.m * 2 - indent) as string[];
    for (const line of lines) {
      room(size * 0.45 + 2);
      doc.text(line, PAGE.m + indent, y);
      y += size * 0.45 + 1.2;
    }
    y += gap;
  };
  const rule = () => {
    room(6);
    doc.setDrawColor(200, 200, 200);
    doc.line(PAGE.m, y, PAGE.w - PAGE.m, y);
    y += 4;
  };
  const heading = (value: string) => {
    room(14);
    text(value, { size: 13, bold: true, colour: INK.primary, gap: 2 });
  };
  const when = (v: string | null | undefined) => (v ? new Date(v).toLocaleString() : "—");
  const pct = (v: number) => `${Math.round(Number(v || 0) * 100)}%`;
  const g = bundle.governance;

  /* Cover */
  text("Evidence Governance™ record", { size: 9, bold: true, colour: INK.primary, gap: 1 });
  text(bundle.entity.name, { size: 20, bold: true, gap: 1 });
  text(
    `${bundle.entity.entity_id} · ${bundle.entity.entity_type} · version ${g.version} · grade ${g.grade}` +
      `${g.promoted ? " · live" : ""}`,
    { size: 9, colour: INK.muted },
  );
  rule();

  heading("Governance summary");
  const summary: Array<[string, string]> = [
    ["Status", g.status],
    ["Review status", g.review_status],
    ["Grade", g.grade],
    ["Confidence", String(g.confidence)],
    ["Weighted domain trust", pct(g.trust_score)],
    ["Evidence coverage", pct(g.coverage)],
    ["Independent domains", String(g.domain_count)],
    ["Supported fields", String(g.supported_fields)],
    ["Freshness", g.freshness + (g.stale ? " (stale)" : "")],
    ["Model", `${g.model ?? "—"} (${g.prompt_version ?? "—"})`],
    ["Evidence hash", g.hash ?? "—"],
    ["Fetched", when(g.fetched_at)],
    ["Last regraded", when(g.regraded_at)],
    ["Reviewed", when(g.reviewed_at)],
    ["Promoted", when(g.promoted_at)],
  ];
  for (const [k, v] of summary) text(`${k}: ${v}`, { size: 9, gap: 0.4 });
  if (g.error) text(`Blocking note: ${g.error}`, { size: 9, colour: [180, 40, 60], gap: 1 });

  heading("Reviewer notes");
  text(bundle.review_notes?.trim() || "No notes recorded.", { size: 9, colour: bundle.review_notes ? INK.body : INK.muted });

  heading("Contradictions across sources");
  if (!bundle.contradictions.length) {
    text("None detected.", { size: 9, colour: INK.muted });
  } else {
    for (const c of bundle.contradictions) {
      text(`${c.axis.replace(/_/g, " ")}: ${c.claims.join(" vs ")}`, { size: 9, bold: true, gap: 0.4 });
      text(c.sources.join("  ·  "), { size: 8, colour: INK.muted, indent: 4 });
    }
  }

  heading("Enriched fields, with cited evidence");
  if (!bundle.fields.length) text("No fields.", { size: 9, colour: INK.muted });
  for (const f of bundle.fields) {
    text(f.field.replace(/_/g, " "), { size: 11, bold: true, gap: 0.6 });
    const value = Array.isArray(f.value) ? f.value.map((v) => `• ${v}`).join("\n") : String(f.value);
    text(value, { size: 9, gap: 1 });
    if (f.citations.length) text(`Cited: ${f.citations.join("  ·  ")}`, { size: 7.5, colour: INK.muted, gap: 0.8 });
    for (const e of f.evidence) {
      text(`“${e.quote}”`, { size: 8, colour: INK.muted, indent: 4, gap: 0.4 });
      text(e.url, { size: 7, colour: INK.primary, indent: 4, gap: 1 });
    }
    rule();
  }

  heading("Provenance");
  if (!bundle.provenance.length) text("No sources recorded.", { size: 9, colour: INK.muted });
  for (const s of bundle.provenance) {
    text(`${s.domain ?? s.url} — ${String(s.trust_tier ?? "unknown").replace(/_/g, " ")} (${s.trust_weight ?? "—"})`, {
      size: 9, bold: true, gap: 0.3,
    });
    if (s.title) text(s.title, { size: 8, colour: INK.muted, indent: 4, gap: 0.3 });
    text(`${s.url} · fetched ${when(s.fetched_at)}`, { size: 7.5, colour: INK.muted, indent: 4, gap: 1 });
  }

  heading("Version history (append-only)");
  if (!bundle.version_history.length) text("No versions recorded.", { size: 9, colour: INK.muted });
  for (const v of bundle.version_history) {
    text(
      `v${v.version} · ${when(v.created_at)} · ${v.grade} · trust ${pct(v.trust_score)} · coverage ${pct(v.coverage)} · ` +
        `${v.domain_count} domains · ${v.supported_fields} fields` +
        `${v.conflicts?.length ? ` · ${v.conflicts.length} conflict(s)` : ""}${v.promoted ? " · promoted" : ""}`,
      { size: 8.5, gap: 0.4 },
    );
  }

  heading("Audit trail");
  if (!bundle.audit_trail.length) text("No audit entries.", { size: 9, colour: INK.muted });
  for (const a of bundle.audit_trail) {
    text(`${when(a.created_at)} · ${a.action} · ${a.actor_kind}`, { size: 8.5, gap: 0.4 });
  }

  rule();
  text(
    `Exported ${when(bundle.exported_at)} · ${bundle.framework}. Descriptive evidence only — ` +
      "psychometrics, Career DNA™, scoring and market intelligence are canonical and untouched.",
    { size: 7.5, colour: INK.muted },
  );
  text(ONET_ATTRIBUTION_DOCUMENT_SENTENCE, { size: 7.5, colour: INK.muted });

  const filename = `evidence-${slug(bundle.entity.name)}-${bundle.entity.entity_id}-v${g.version}.pdf`;
  doc.save(filename);
  return filename;
}
